import { NonNullable } from '@pubstack/common/src/assertion'
import { useAtomValue } from 'jotai'
import { useEffect } from 'react'
import { useLocation } from 'react-router-dom'
import { useAuthenticatedUser } from '~/auth/user.hooks'
import { useLocalStorage } from '~/hooks/useLocalStorage'
import { useSparkForPubStaffAccess } from '~/hooks/useSparkForPub'
import { useAlerts } from '~/modules/alerts/hooks/useAlerts'
import { currentScopeState } from '~/state'
import { sparkConsoleUrl } from '~/utils/environment'
import { contactUsUrl } from '~/utils/hubspot'
import { nodeStaffStateOfScope } from '~/hooks/useNodeStaffAccess'
import { useCanAccessRoute, useIsNodeRoute } from '~/utils/router'
import { buildSections, SimpleItem } from './menu.config'
import { PureMenu, PureMenuItem, PureMenuSection } from './PureMenu'

const appendTraillingSlash = (str: string) => `${str}${str.endsWith('/') ? '' : '/'}`
const addActive =
  (currentLocation: string) =>
  <T extends SimpleItem>(item: T) => ({
    ...item,
    active: appendTraillingSlash(currentLocation).startsWith(appendTraillingSlash(item.to)),
  })

const useMenuOpenState = () => {
  const [isGroupOpen, setIsGroupOpen] = useLocalStorage<{ [key: string]: boolean }>({ key: 'menuOpenGroups', defaultValue: {} })
  const key = (section: string, item: string) => `${section}-${item}`
  const isOpen = (section: string, item: string) => isGroupOpen[key(section, item)]
  const toggle = (section: string, item: string) => setIsGroupOpen({ ...isGroupOpen, [key(section, item)]: !isGroupOpen[key(section, item)] })
  const open = (section: string, item: string) => setIsGroupOpen({ ...isGroupOpen, [key(section, item)]: true })
  const close = (section: string, item: string) => setIsGroupOpen({ ...isGroupOpen, [key(section, item)]: false })

  return {
    isOpen,
    open,
    close,
    toggle,
  }
}

const SPARK_FOR_PUB_PATH = '/analytics/spark-for-pub'

const Menu = () => {
  const location = useLocation()
  const canAccessRoute = useCanAccessRoute()
  const isNodeRoute = useIsNodeRoute()
  const user = useAuthenticatedUser()
  const currentScope = useAtomValue(currentScopeState)
  const alerts = useAlerts()
  const alertsNotification = (alerts[user.currentScopeId] || []).filter((alert) => !alert.acknowledge).length
  const { open, toggle, isOpen } = useMenuOpenState()
  const { isDashboardEnabled: sparkForPubUiEnabled, isStaffOnSparkForPub } = useSparkForPubStaffAccess()
  // Onboarded on Node but not yet opened to its users: staff only. The scope's own features, not the
  // viewer's, since staff have every feature synthesised.
  const { isStaffOnNode } = nodeStaffStateOfScope(currentScope, user)
  const handleItems = (items: SimpleItem[]): PureMenuItem[] => {
    return items
      .filter((item) => canAccessRoute(item.to))
      .filter((item) => item.show ?? true)
      .map((item) => ({ ...item, staffOnly: (isStaffOnNode && isNodeRoute(item.to)) || (isStaffOnSparkForPub && item.to === SPARK_FOR_PUB_PATH) }))
      .map(addActive(location.pathname))
  }

  const rawSections = buildSections({ alertsNotification, sparkForPubUiEnabled })

  const sections: PureMenuSection[] = rawSections
    .map((section) => ({
      ...section,
      items: section.items
        .map((item) => {
          if (item.type === 'group') {
            return {
              ...item,
              items: handleItems(item.items),
              open: isOpen(section.title, item.name),
              onClick: () => toggle(section.title, item.name),
            }
          } else {
            return handleItems([item]).pop()
          }
        })
        .filter(NonNullable)
        .filter((item) => (item.type === 'group' ? item.items.length > 0 : true)),
    }))
    .filter((section) => section.items.length > 0)

  const contactUs = () => {
    window.open(contactUsUrl({ email: user.profile.email, scopeName: currentScope.name }), '_blank')
  }

  const openSparkConsole = () => {
    window.open(sparkConsoleUrl(), '_blank')
  }

  const bottomItems: PureMenuItem[] = (
    [
      ...(canAccessRoute('/settings') ? [{ name: 'Settings', to: '/settings' }] : []),
      user.isAdmin ? { name: 'Admin', to: `/admin/scopes/${user.currentScopeId}` } : { name: 'Contact us', to: 'unused', isLink: true, onClick: contactUs },
      // Spark Console is a separate app with no route of its own here, so this is a plain external redirect.
      ...(user.isAdmin ? [{ name: 'Spark admin', to: 'unused', isLink: true, onClick: openSparkConsole }] : []),
    ] as SimpleItem[]
  ).map(addActive(location.pathname))

  useEffect(() => {
    sections.forEach((section) => {
      section.items.forEach((item) => {
        if (item.type === 'group' && item.items.some((i) => i.active)) {
          open(section.title, item.name)
        }
      })
    })
  }, [location])

  return <PureMenu sections={sections} bottomItems={bottomItems} />
}

export default Menu
